import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faArrowUpArrowDown, faPencil } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";

import { LocaleCurrency } from "helpers/Locale";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import { ButtonNew as Button } from "components/buttonNew";
import Spin from "components/spin/spin";
import { Badge } from "components/badge/Badge";
import DataTable from "../../../../../../components/dataTable/DataTable";

import NewCustomerCreditModal from "./NewCustomerCreditModal";
import CustomerCreditAdjustModal from "./CustomerCreditAdjustModal";
import CustomerCreditEmptyState from "./CustomerCreditEmptyState";
import CustomerCreditTransactionNoteEditor from "./CustomerCreditTransactionNoteEditor";
import {
  getCreditTransactionKindBadge,
  getCreditTransactionNoteDisplay,
} from "./creditTransactionUtils";
import {
  GetCreditBookTransactions,
  GetCustomerCredit,
  ICreditTransactionRes,
  isPaginatedCreditTransactions,
  PostCustomerCreditBook,
  PutCreditBookTransactionNote,
} from "api/rpc/2024-04/facilityAdmin/customer/customerCredit";
import { PostAdjustment, PutCreditBook } from "api/rpc/2024-04/facilityAdmin/client/admin/creditBook";

import { IPrizeAccount } from "pages/secure/facility/settings/prizeAccounts/PrizeAccount";

import "./CustomerCredit.scss";
import classNames from "classnames";

const TRANSACTIONS_LIMIT = 10;

function mapTransactionDateSortToApi(direction: "asc" | "desc"): "asc" | "desc" {
  return direction === "asc" ? "desc" : "asc";
}

function getVisiblePages(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: Array<number | "ellipsis"> = [1];

  if (currentPage > 3) {
    pages.push("ellipsis");
  }

  const rangeStart = Math.max(2, currentPage - 1);
  const rangeEnd = Math.min(totalPages - 1, currentPage + 1);

  for (let page = rangeStart; page <= rangeEnd; page += 1) {
    pages.push(page);
  }

  if (currentPage < totalPages - 2) {
    pages.push("ellipsis");
  }

  pages.push(totalPages);
  return pages;
}

interface IProps {
  customerId: string;
}

export default function CustomerCredit(props: IProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { authStore } = useAppSelector(store => store);
  const permissions = authStore.user?.permissions;

  const [creditState, setCreditState] = useState<IPrizeAccount[] | null>(null);
  const [creditTransactionState, setCreditTransactionState] = useState<ICreditTransactionRes[] | null>(null);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionOffset, setTransactionOffset] = useState(0);
  const [transactionSortDirection, setTransactionSortDirection] = useState<"asc" | "desc">("desc");
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [showNewCreditBookingModal, setShowNewCreditBookingModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [editingNote, setEditingNote] = useState<{
    transaction: ICreditTransactionRes;
    anchorRect: DOMRect;
  } | null>(null);
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingTitleAnchor, setEditingTitleAnchor] = useState<DOMRect | null>(null);
  const [titleSaving, setTitleSaving] = useState(false);
  const customer_id = Number(props.customerId);

  const creditBook = creditState?.[0] ?? null;
  const transactions = creditTransactionState ?? [];
  const hasNoTransactions = transactionTotal === 0 && !transactionsLoading;
  const canAdjustBalance = Boolean(permissions?.credit_books_make_adjustments);
  const currentPage = Math.floor(transactionOffset / TRANSACTIONS_LIMIT) + 1;
  const totalPages = Math.max(1, Math.ceil(transactionTotal / TRANSACTIONS_LIMIT));
  const visiblePages = getVisiblePages(currentPage, totalPages);
  const hasMoreTransactions = transactionOffset + TRANSACTIONS_LIMIT < transactionTotal;

  useEffect(() => {
    setTransactionOffset(0);

    const source = axios.CancelToken.source();
    void loadCustomerCredit(source.token);
    return () => source.cancel();
  }, [customer_id]);

  useEffect(() => {
    if (!creditBook?.id || creditState === null) {
      return;
    }

    const source = axios.CancelToken.source();
    void loadCreditTransactions(creditBook.id, transactionOffset, transactionSortDirection, source.token);
    return () => source.cancel();
  }, [creditBook?.id, transactionOffset, transactionSortDirection, creditState]);

  async function loadCustomerCredit(token?: CancelToken) {
    setCreditState(null);
    setCreditTransactionState(null);
    setTransactionTotal(0);

    const customerCreditRes = await GetCustomerCredit({ customer_id }, token ? false : true, token);
    if (token?.reason) {
      return;
    }
    if (customerCreditRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading Customer Credit Book")));
      setCreditState([]);
      setCreditTransactionState([]);
      return;
    }

    const books = (customerCreditRes.data as IPrizeAccount[]) ?? [];
    setCreditState(books);

    if (!books[0]?.id) {
      setCreditTransactionState([]);
    }
  }

  async function loadCreditTransactions(
    credit_book_id: number,
    offset: number,
    sortDirection: "asc" | "desc",
    token?: CancelToken,
  ) {
    setTransactionsLoading(true);

    const creditTransactionRes = await GetCreditBookTransactions(
      {
        credit_book_id,
        limit: TRANSACTIONS_LIMIT,
        offset,
        with_total: true,
        sort_by: "processed_at_local",
        sort_direction: sortDirection,
      },
      token ? false : true,
      token,
    );

    if (token?.reason) {
      setTransactionsLoading(false);
      return;
    }

    setTransactionsLoading(false);

    if (creditTransactionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading Credit Book Transactions")));
      setCreditTransactionState([]);
      setTransactionTotal(0);
      return;
    }

    const payload = creditTransactionRes.data;
    if (isPaginatedCreditTransactions(payload)) {
      setCreditTransactionState(payload.transactions ?? []);
      setTransactionTotal(payload.total ?? 0);
      return;
    }

    const fallbackTransactions = payload ?? [];
    setCreditTransactionState(fallbackTransactions);
    setTransactionTotal(fallbackTransactions.length);
  }

  async function createNewBookingCredit(title: string) {
    if (title === "") {
      dispatch(showError(t("Title must not be empty")));
      return;
    }

    const creditRes = await PostCustomerCreditBook({ title, customer_id }, true);
    if (creditRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating new Credit Book")));
      return;
    }
    setShowNewCreditBookingModal(false);
    setTransactionOffset(0);
    void loadCustomerCredit();
  }

  async function saveTransactionNote(note: string) {
    if (!editingNote) {
      return;
    }

    setNoteSaving(true);
    const res = await PutCreditBookTransactionNote(
      {
        id: editingNote.transaction.id,
        note,
      },
      true,
    );
    setNoteSaving(false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating transaction note")));
      return;
    }

    dispatch(showSuccess(t("Transaction note updated successfully")));
    setEditingNote(null);

    if (creditBook?.id) {
      void loadCreditTransactions(creditBook.id, transactionOffset, transactionSortDirection);
    }
  }

  async function saveCreditBookTitle(title: string) {
    if (!creditBook?.id) {
      return;
    }

    const trimmedTitle = title.trim();
    if (trimmedTitle === "") {
      dispatch(showError(t("Title must not be empty")));
      return;
    }

    setTitleSaving(true);
    const res = await PutCreditBook({ id: creditBook.id, title: trimmedTitle }, true);
    setTitleSaving(false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating credit book title")));
      return;
    }

    dispatch(showSuccess(t("Credit book title updated successfully")));
    setEditingTitleAnchor(null);
    setCreditState(prev =>
      prev ? prev.map((book, index) => (index === 0 ? { ...book, title: trimmedTitle } : book)) : prev,
    );
  }

  async function adjustCreditBook(amount: string, note: string) {
    if (!creditBook?.id) {
      dispatch(showError(t("No credit book found")));
      return;
    }

    const res = await PostAdjustment({ credit_book_id: creditBook.id, amount, note }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error adjusting credit book balance")));
      return;
    }

    setShowAdjustModal(false);
    setTransactionOffset(0);
    void loadCustomerCredit();
  }

  function handleTransactionPageChange(direction: "prev" | "next") {
    if (direction === "prev") {
      setTransactionOffset(prev => Math.max(prev - TRANSACTIONS_LIMIT, 0));
      return;
    }

    setTransactionOffset(prev => prev + TRANSACTIONS_LIMIT);
  }

  function handleTransactionPageSelect(page: number) {
    setTransactionOffset((page - 1) * TRANSACTIONS_LIMIT);
  }

  function renderTransactionsTable() {
    return (
      <div className="customer-credit-transactions">
        <DataTable<ICreditTransactionRes>
          title={
            <div className="customer-credit-transactions__title">
              {t("Transactions")}
              <Badge type="gray" size="small" className="customer-credit-transactions__count-badge">
                {transactionTotal}
              </Badge>
            </div>
          }
          columns={[
            { label: t("Transaction date"), id: "processed_at_local", width: "18%" },
            { label: t("Amount"), width: "12%" },
            { label: t("Order #"), width: "12%" },
            { label: t("Transaction Type"), width: "18%" },
            { label: t("Note") },
          ]}
          sort={(key, direction) => {
            if (key === "processed_at_local") {
              setTransactionSortDirection(mapTransactionDateSortToApi(direction));
              setTransactionOffset(0);
            }
          }}
          hideHeader={false}
          noRoundedTop
          loading={transactionsLoading}
        >
          {hasNoTransactions ? (
            <tr className="customer-credit-empty-row">
              <td colSpan={5}>
                <CustomerCreditEmptyState
                  onAdjustBalance={() => setShowAdjustModal(true)}
                  adjustDisabled={!canAdjustBalance}
                />
              </td>
            </tr>
          ) : (
            transactions.map(transaction => {
              const noteText = getCreditTransactionNoteDisplay(transaction.note);

              return (
                <tr key={transaction.id}>
                  <td>
                    {transaction.processed_at_local
                      ? moment(transaction.processed_at_local).format("MMM DD, YYYY")
                      : "-"}
                  </td>
                  <td className={classNames({
                    "line-through": transaction.voided_at !== null,
                  })}>
                    <LocaleCurrency currency={transaction.currency} amount={transaction.amount} />
                  </td>
                  <td>{transaction.order_number || "-"} {transaction.voided_at !== null ? ' - ' + t("Voided") : ""}</td>
                  <td>{getCreditTransactionKindBadge(transaction.kind)}</td>
                  <td className="customer-credit-transactions__note-cell">
                    <button
                      type="button"
                      className={`customer-credit-transactions__note${noteText ? "" : " is-placeholder"}`}
                      title={noteText ?? undefined}
                      onClick={event => {
                        const noteCell = event.currentTarget.closest("td");
                        setEditingNote({
                          transaction,
                          anchorRect: (noteCell ?? event.currentTarget).getBoundingClientRect(),
                        });
                      }}
                    >
                      {noteText ? noteText : t("Add a note for your records...")}
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </DataTable>

        {!hasNoTransactions ? (
          <div className="customer-credit-pagination">
            <Button
              type="secondary"
              size="small"
              disabled={transactionOffset === 0 || transactionsLoading}
              onClick={() => handleTransactionPageChange("prev")}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              {t("Previous")}
            </Button>

            <div className="customer-credit-pagination__pages">
              {visiblePages.map((page, index) =>
                page === "ellipsis" ? (
                  <span key={`ellipsis-${index}`} className="customer-credit-pagination__ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    type="button"
                    className={`customer-credit-pagination__page${page === currentPage ? " is-active" : ""}`}
                    onClick={() => handleTransactionPageSelect(page)}
                    disabled={page === currentPage || transactionsLoading}
                  >
                    {page}
                  </button>
                ),
              )}
            </div>

            <Button
              type="secondary"
              size="small"
              disabled={!hasMoreTransactions || transactionsLoading}
              onClick={() => handleTransactionPageChange("next")}
            >
              {t("Next")}
              <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  if (creditState === null || (creditBook && creditTransactionState === null)) {
    return (
      <div className="customer-credit-loading">
        <span>
          <Spin />
        </span>
      </div>
    );
  }

  if (!creditBook) {
    return (
      <div className="customer-credit-tab">
        <div className="customer-credit-no-book">
          <p>{t("This customer does not have a credit book yet.")}</p>
          <Button type="primary" size="medium" onClick={() => setShowNewCreditBookingModal(true)}>
            {t("New Credit Book")}
          </Button>
        </div>

        <NewCustomerCreditModal
          isVisible={showNewCreditBookingModal}
          customerId={customer_id}
          onClose={() => setShowNewCreditBookingModal(false)}
          onOk={createNewBookingCredit}
        />
      </div>
    );
  }

  return (
    <div className="customer-credit-tab">
      <div className="customer-credit-balance-card">
        <div className="customer-credit-balance-card__left">
          <div className="customer-credit-balance-card__content">
            <p className="customer-credit-balance-card__label">{t("Title")}</p>
            <div className="customer-credit-balance-card__title-row">
              <div className="customer-credit-balance-card__amount">{creditBook.title || "-"}</div>
              <button
                type="button"
                className="customer-credit-balance-card__edit"
                aria-label={t("Edit")}
                onClick={event => {
                  setEditingTitleAnchor(event.currentTarget.getBoundingClientRect());
                }}
              >
                <FontAwesomeIcon icon={faPencil} />
              </button>
            </div>
          </div>
          <div className="customer-credit-balance-card__divider" aria-hidden="true" />
          <div className="customer-credit-balance-card__content">
            <p className="customer-credit-balance-card__label">{t("Current balance")}</p>
            <div className="customer-credit-balance-card__amount">
              <LocaleCurrency amount={creditBook.balance} currency={creditBook.currency} />
            </div>
          </div>
        </div>
        <Button
          type="secondary"
          size="medium"
          disabled={!canAdjustBalance}
          onClick={() => setShowAdjustModal(true)}
        >
          <FontAwesomeIcon icon={faArrowUpArrowDown} className="mr-2" />
          {t("Adjust balance")}
        </Button>
      </div>

      {renderTransactionsTable()}

      <NewCustomerCreditModal
        isVisible={showNewCreditBookingModal}
        customerId={customer_id}
        onClose={() => setShowNewCreditBookingModal(false)}
        onOk={createNewBookingCredit}
      />

      <CustomerCreditAdjustModal
        open={showAdjustModal}
        onClose={() => setShowAdjustModal(false)}
        onOk={adjustCreditBook}
      />

      <CustomerCreditTransactionNoteEditor
        open={Boolean(editingNote)}
        anchorRect={editingNote?.anchorRect ?? null}
        value={editingNote?.transaction.note ?? ""}
        saving={noteSaving}
        onClose={() => setEditingNote(null)}
        onSave={note => void saveTransactionNote(note)}
      />

      <CustomerCreditTransactionNoteEditor
        open={Boolean(editingTitleAnchor)}
        anchorRect={editingTitleAnchor}
        value={creditBook.title ?? ""}
        placeholder={t("Title")}
        rows={1}
        openTo="right"
        saving={titleSaving}
        onClose={() => setEditingTitleAnchor(null)}
        onSave={title => void saveCreditBookTitle(title)}
      />
    </div>
  );
}
